<template>
    <div>
        <b-card>
            <b-card-title>Email Preferences</b-card-title>
            <p class="mb-2">
                Manage which types of emails you receive from us. Service emails are always enabled.
            </p>

            <!-- opt in / opt out of all -->
            <div class="py-2 border-bottom">
                <div class="font-weight-bold mb-1">
                    Bulk Actions
                </div>
                <small class="text-muted d-block mb-2">Quickly enable or disable all non-essential emails. Service notifications are always sent.</small>
                <b-button
                    v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                    variant="outline-success"
                    class="mr-1"
                    @click="optInAll"
                >
                    Opt in to all
                </b-button>
                <b-button
                    v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                    variant="outline-secondary"
                    @click="optOutAll"
                >
                    Opt out of all
                </b-button>
            </div>

            <!-- report email frequency -->
            <div class="py-2 border-bottom">
                <div class="font-weight-bold mb-1">
                    Report Publication alerts
                </div>
                <p class="mb-1">
                    Please select your preferred email frequency
                   </p>
                <b-row>
                    <b-col
                        cols="12"
                        class="mb-2"
                    >
                        <b-form-radio
                            v-model="localOptions.reportFrequency"
                            name="report-frequency"
                            value="AllEmails"
                            class="cursor-pointer"
                        >
                            When a new product is added
                        </b-form-radio>
                    </b-col>
                    <b-col
                        cols="12"
                        class="mb-2"
                    >
                        <b-form-radio
                            v-model="localOptions.reportFrequency"
                            name="report-frequency"
                            value="DailyDigest"
                            class="cursor-pointer"
                        >
                            Daily digest
                        </b-form-radio>
                    </b-col>
                    <b-col
                        cols="12"
                        class="mb-2"
                    >
                        <b-form-radio
                            v-model="localOptions.reportFrequency"
                            name="report-frequency"
                            value="WeeklyDigest"
                            class="cursor-pointer"
                        >
                            Weekly digest (on Monday)
                        </b-form-radio>
                    </b-col>
                    <b-col
                        cols="12"
                        class="mb-2"
                    >
                        <b-form-radio
                            v-model="localOptions.reportFrequency"
                            name="report-frequency"
                            value="Never"
                            class="cursor-pointer"
                        >
                            Never
                        </b-form-radio>
                    </b-col>
                </b-row>
            </div>

            <!-- individual preferences -->
            <b-row
                v-for="item in emailPreferences"
                v-show="!['promotion', 'hubUpdate', 'premiumUpdate'].includes(item.key)"
                :key="item.key"
                class="align-items-center py-2 border-bottom"
            >
                <b-col
                    cols="auto"
                    class="d-flex align-items-center"
                >
                    <b-form-checkbox
                        v-model="item.enabled"
                        :disabled="item.disabled"
                        switch
                        size="lg"
                    />
                </b-col>
                <b-col>
                    <div class="font-weight-bold">
                        {{ item.label }}
                    </div>
                    <small class="text-muted">{{ item.description }}</small>
                </b-col>
            </b-row>

            <!-- save -->
            <div class="mt-2">
                <b-button
                    v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                    variant="primary"
                    :disabled="saveLoading"
                    @click="saveChanges"
                >
                    <b-spinner
                        v-if="saveLoading"
                        small
                    />
                    <span v-else>Save changes</span>
                </b-button>
            </div>
        </b-card>
    </div>
</template>

<script>
import {
  BButton, BRow, BCol, BCard, BCardTitle, BFormRadio, BSpinner, BFormCheckbox,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'

export default {
  components: {
    BButton,
    BRow,
    BCol,
    BCard,
    BCardTitle,
    BFormRadio,
    BSpinner,
    BFormCheckbox,
  },
  directives: {
    Ripple,
  },
  props: {
    notificationData: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      localOptions: {
        reportFrequency: this.notificationData?.reportFrequency || 'AllEmails',
        ...JSON.parse(JSON.stringify(this.notificationData)),
      },
      saveLoading: false,
      emailPreferences: [
        {
          key: 'service',
          label: 'Service Emails',
          description: 'Essential account and service notifications. These are always enabled.',
          enabled: true,
          disabled: true,
        },
        {
          key: 'promotion',
          label: 'Promotions',
          description: 'Special offers, discounts, and promotional campaigns.',
          enabled: false,
          disabled: false,
        },
        {
          key: 'hubUpdate',
          label: 'Hub Updates',
          description: 'News and updates from the StructuredEdge Hub.',
          enabled: false,
          disabled: false,
        },
        {
          key: 'premiumUpdate',
          label: 'Premium Updates',
          description: 'Exclusive updates and content for premium subscribers.',
          enabled: false,
          disabled: false,
        },
      ],
    }
  },
  watch: {
    notificationData: {
      handler(newVal) {
        if (!newVal) return
        if (newVal.reportFrequency) {
          this.localOptions = {
            reportFrequency: newVal.reportFrequency,
            ...JSON.parse(JSON.stringify(newVal)),
          }
        }
        const prefMap = {
          promotion: 'emailPromotions',
          hubUpdate: 'emailHubUpdates',
          premiumUpdate: 'emailPremiumUpdates',
        }
        this.emailPreferences = this.emailPreferences.map(p => {
          const field = prefMap[p.key]
          if (field && newVal[field] !== undefined) {
            return { ...p, enabled: newVal[field] === true || newVal[field] === 'true' }
          }
          return p
        })
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    optInAll() {
      this.localOptions.reportFrequency = 'AllEmails'
      this.emailPreferences = this.emailPreferences.map(p => ({ ...p, enabled: true, disabled: p.key === 'service' }))
    },
    optOutAll() {
      this.localOptions.reportFrequency = 'Never'
      this.emailPreferences = this.emailPreferences.map(p => ({ ...p, enabled: p.key === 'service', disabled: p.key !== 'service' }))
    },
    saveChanges() {
      this.saveLoading = true

      const methodMap = {
        promotion: 'setEmailPromotions',
        hubUpdate: 'setEmailHubUpdates',
        premiumUpdate: 'setEmailPremiumUpdates',
      }

      const preferenceRequests = this.emailPreferences
        .filter(p => methodMap[p.key])
        .map(p => this.$http.get('account.cfc', { params: { method: methodMap[p.key], subscribe: p.enabled } }))

      const frequencyRequest = this.$http.get('account.cfc', {
        params: {
          method: 'emailfrequencychange',
          emailfrequency: this.localOptions.reportFrequency,
        },
      })

      Promise.all([frequencyRequest, ...preferenceRequests])
        .then(responses => {
          this.saveLoading = false
          const allSucceeded = responses.every(r => r.data.success)
          if (allSucceeded) {
            this.$bvToast.toast('Your email preferences have been updated successfully', {
              title: 'Success',
              variant: 'success',
              solid: true,
            })
          } else {
            this.$bvToast.toast('Some preferences could not be updated. Please try again', {
              title: 'Failed',
              variant: 'danger',
              solid: true,
            })
          }
        })
        .catch(() => {
          this.saveLoading = false
          this.$bvToast.toast('An error occurred. Please try again', {
            title: 'Error',
            variant: 'danger',
            solid: true,
          })
        })
    },
  },
}
</script>

<style scoped>
.cursor-pointer >>> .custom-control-label,
.cursor-pointer >>> .custom-control-input {
  cursor: pointer !important;
}
</style>
